// <s-phuljhadi>: "Light a phuljhadi". A button sends this element the `--light` command; it
// opens its native <dialog> with a zoom-in, then burns a sparkler on a <canvas>: golden
// branching sparks, a sizzling crackle and a firecracker bang when it catches and when it
// finishes. Sounds are synthesized with the Web Audio API (no audio files).
//
// Required markup inside the element:
//   dialog                     — the popup (closed with a native commandfor/close button)
//   canvas[data-sparkler]      — drawing surface
//   [data-relight]             — optional button to light another one
// Reduced-motion users get a calm, slow sparkler without the bang or screen flash.
class SPhuljhadi extends HTMLElement {
  #ac;
  #raf = 0;
  #audio = null;
  #hiss = null;
  #sparks = [];
  #start = 0;
  #burn = 9000; // ms the sparkler burns
  #finished = false;
  #nextCrackle = 0;
  #calm = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.dialog = this.querySelector("dialog");
    this.canvas = this.querySelector("canvas[data-sparkler]");

    this.addEventListener(
      "command",
      (event) => {
        if (event.command === "--light") this.#open();
      },
      opts,
    );
    this.querySelector("[data-relight]")?.addEventListener("click", () => this.#light(), opts);
    this.dialog?.addEventListener("close", () => this.#stop(), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  #open() {
    if (!this.dialog || !this.canvas) return;
    this.dialog.showModal();
    if (!this.#calm) {
      this.dialog.animate(
        [
          { transform: "scale(0.3)", opacity: 0 },
          { transform: "scale(1.04)", opacity: 1, offset: 0.7 },
          { transform: "scale(1)", opacity: 1 },
        ],
        { duration: 480, easing: "cubic-bezier(0.2, 0.9, 0.3, 1.2)" },
      );
    }
    this.#light();
  }

  #light() {
    cancelAnimationFrame(this.#raf);
    this.#stopHiss();
    this.#sizeCanvas();
    this.#sparks = [];
    this.#finished = false;
    this.#start = performance.now();
    this.#nextCrackle = this.#start;
    this.#ensureAudio();
    if (!this.#calm) this.#bang(0.9);
    this.#startHiss();
    if (!this.#calm) this.#burst(this.#tip(0), 60, 1.6);
    this.#raf = requestAnimationFrame((t) => this.#frame(t));
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#stopHiss();
  }

  #sizeCanvas() {
    // clientWidth/Height ignore the zoom-in transform, so the canvas is sized at full scale.
    const rect = { width: this.canvas.clientWidth, height: this.canvas.clientHeight };
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.ctx = this.canvas.getContext("2d");
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = rect.width;
    this.h = rect.height;
  }

  // Sparkler geometry: a thin wire held at the bottom, angled slightly, burning from the top down.
  #geometry() {
    const base = { x: this.w * 0.42, y: this.h * 0.96 };
    const top = { x: this.w * 0.55, y: this.h * 0.32 };
    return { base, top };
  }

  #tip(progress) {
    const { base, top } = this.#geometry();
    const coatEnd = 0.72; // coated part ends 72% of the way down to the handle
    const p = Math.min(1, progress) * coatEnd;
    return { x: top.x + (base.x - top.x) * p, y: top.y + (base.y - top.y) * p };
  }

  #frame(now) {
    const ctx = this.ctx;
    const progress = (now - this.#start) / this.#burn;
    const burning = progress < 1;
    const tip = this.#tip(progress);
    const { base, top } = this.#geometry();

    // Fade the previous frame toward transparent for light trails (no background is painted).
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.globalCompositeOperation = "source-over";

    // Wire handle (bare) and the remaining coated part.
    ctx.lineCap = "round";
    ctx.strokeStyle = "#6b6b70";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(base.x, base.y);
    ctx.lineTo(tip.x, tip.y);
    ctx.stroke();
    if (burning) {
      const coatEnd = this.#tip(1);
      ctx.strokeStyle = "#3a3438";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(tip.x, tip.y);
      ctx.lineTo(coatEnd.x, coatEnd.y);
      ctx.stroke();
      // Burnt ash above the tip.
      ctx.strokeStyle = "rgba(120,110,110,0.5)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(top.x, top.y);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
    }

    ctx.globalCompositeOperation = "lighter";

    if (burning) {
      // Hot glowing tip.
      const glowR = this.#calm ? 22 : 34 + Math.random() * 10;
      const g = ctx.createRadialGradient(tip.x, tip.y, 0, tip.x, tip.y, glowR);
      g.addColorStop(0, "rgba(255,255,240,1)");
      g.addColorStop(0.25, "rgba(255,220,120,0.9)");
      g.addColorStop(1, "rgba(255,140,0,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(tip.x, tip.y, glowR, 0, Math.PI * 2);
      ctx.fill();

      // Emit sparks.
      const emit = this.#calm ? 4 : 22;
      for (let i = 0; i < emit; i++) this.#sparks.push(this.#newSpark(tip, this.#calm ? 1.2 : 2.6));

      // Crackle: random bright pops with a burst and a snap sound.
      if (!this.#calm && now >= this.#nextCrackle) {
        this.#crackle();
        if (Math.random() < 0.35) this.#burst(tip, 10 + Math.random() * 12, 1.4);
        this.#nextCrackle = now + 25 + Math.random() * 90;
      }
    } else if (!this.#finished) {
      this.#finished = true;
      this.#stopHiss();
      if (!this.#calm) {
        this.#bang(1);
        this.#burst(tip, 140, 3.2, true);
      }
    }

    // Update and draw sparks as short streaks.
    const next = [];
    for (const s of this.#sparks) {
      s.life -= 1;
      if (s.life <= 0) continue;
      const px = s.x;
      const py = s.y;
      s.vy += 0.06;
      s.vx *= 0.985;
      s.vy *= 0.985;
      s.x += s.vx;
      s.y += s.vy;
      // Branching: some sparks fork into tiny stars mid-flight.
      if (s.branch && s.life === s.branchAt) {
        for (let k = 0; k < 4; k++) next.push(this.#newSpark(s, 1.4, 10 + Math.random() * 8, s.hue));
      }
      const alpha = Math.min(1, s.life / 18);
      // Rich gold so sparks stay visible over light page backgrounds too.
      ctx.strokeStyle = `hsla(${s.hue}, 100%, ${55 + Math.random() * 25}%, ${alpha})`;
      ctx.lineWidth = s.width;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(s.x, s.y);
      ctx.stroke();
      next.push(s);
    }
    this.#sparks = next.length > 1600 ? next.slice(-1600) : next;

    if (burning || this.#sparks.length) {
      this.#raf = requestAnimationFrame((t) => this.#frame(t));
    }
  }

  #newSpark(from, speed, life, hue) {
    const a = Math.random() * Math.PI * 2;
    const v = (0.6 + Math.random()) * speed;
    return {
      x: from.x,
      y: from.y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v - 0.4,
      life: life ?? 16 + Math.random() * 26,
      width: 0.8 + Math.random() * 1.4,
      hue: hue ?? 38 + Math.random() * 18, // gold → warm yellow
      branch: !life && Math.random() < 0.12,
      branchAt: 8 + Math.floor(Math.random() * 6),
    };
  }

  #burst(at, count, speed, colorful = false) {
    const hues = [38, 50, 320, 10, 280];
    for (let i = 0; i < count; i++) {
      const hue = colorful ? hues[i % hues.length] : undefined;
      this.#sparks.push(this.#newSpark(at, speed * (0.6 + Math.random()), 30 + Math.random() * 30, hue));
    }
  }

  // ---- Sound ----

  #ensureAudio() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    this.#audio ??= new AudioCtx();
    if (this.#audio.state === "suspended") this.#audio.resume();
    if (!this.noiseBuffer) {
      const len = this.#audio.sampleRate * 2;
      const buf = this.#audio.createBuffer(1, len, this.#audio.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buf;
    }
    return this.#audio;
  }

  #noise(duration, offset = 0) {
    const ctx = this.#audio;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = duration > 1.5;
    src.start(ctx.currentTime + offset, Math.random() * 1.5, src.loop ? undefined : duration);
    return src;
  }

  // Continuous sizzle while the sparkler burns.
  #startHiss() {
    const ctx = this.#audio;
    if (!ctx) return;
    const src = this.#noise(this.#burn / 1000 + 1);
    const band = ctx.createBiquadFilter();
    band.type = "highpass";
    band.frequency.value = 3500;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(this.#calm ? 0.04 : 0.09, ctx.currentTime + 0.3);
    src.connect(band).connect(gain).connect(ctx.destination);
    this.#hiss = { src, gain };
  }

  #stopHiss() {
    if (!this.#hiss || !this.#audio) return;
    const { src, gain } = this.#hiss;
    const t = this.#audio.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(gain.gain.value, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
    try {
      src.stop(t + 0.3);
    } catch {}
    this.#hiss = null;
  }

  // A sharp little snap.
  #crackle() {
    const ctx = this.#audio;
    if (!ctx) return;
    const t = ctx.currentTime;
    const src = this.#noise(0.04);
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 2000 + Math.random() * 4000;
    band.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.25 + Math.random() * 0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.03 + Math.random() * 0.03);
    src.connect(band).connect(gain).connect(ctx.destination);
  }

  // Firecracker bang: a noise crack plus a low thump, with a few trailing crackles.
  #bang(level) {
    const ctx = this.#audio;
    if (!ctx) return;
    const t = ctx.currentTime;

    const crack = this.#noise(0.6);
    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.setValueAtTime(6000, t);
    low.frequency.exponentialRampToValueAtTime(300, t + 0.5);
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.9 * level, t);
    cg.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
    crack.connect(low).connect(cg).connect(ctx.destination);

    const thump = ctx.createOscillator();
    thump.type = "sine";
    thump.frequency.setValueAtTime(140, t);
    thump.frequency.exponentialRampToValueAtTime(40, t + 0.3);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.8 * level, t);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    thump.connect(tg).connect(ctx.destination);
    thump.start(t);
    thump.stop(t + 0.4);

    for (let i = 0; i < 8; i++) setTimeout(() => this.#crackle(), 120 + i * (40 + Math.random() * 60));
  }
}

if (!customElements.get("s-phuljhadi")) customElements.define("s-phuljhadi", SPhuljhadi);
